import type { AccountPermission } from '@customerio/hydra-permissions';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';

/**
 * Check a permission that must remain available on paused accounts.
 *
 * Paused accounts have `write_access: false` to lock down the general UI (campaigns, workflows).
 * `hasPermission` goes through `checkPermission`, which rejects all non-view permissions when
 * `write_access` is false — blocking billing self-service actions (cancel, update card, retry
 * payment) that paused customers need.
 *
 * For non-masquerade sessions on paused accounts, this bypasses the `write_access` gate by
 * checking `account_permissions` directly. Masquerade sessions still go through `hasPermission`,
 * which resolves masq access (None/Read/ReadWrite) before `write_access` is ever checked.
 */
export function usePauseSafePermission(permission: AccountPermission): boolean {
  const { account, user } = useAuth();
  const { hasPermission } = usePermissions();

  if (account.is_paused && !account.is_masq) {
    return user.account_permissions.includes(permission);
  }
  return hasPermission(permission);
}
